import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AddressDisplay } from "@/components/AddressDisplay";
import { CONSOLE } from "@/constants/testIds";
import { AlertTriangle, ShieldOff, Pause, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";

export default function Compliance() {
  const { token, blacklist, actions } = useApp();
  const [addr, setAddr] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const activeList = blacklist.filter((b) => b.active);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Compliance & Security</div>
        <h1 className="text-3xl font-semibold tracking-tight">Pause & Blacklist</h1>
      </div>

      {/* Global pause */}
      <div className={`bg-white border rounded-md p-5 flex flex-wrap items-center justify-between gap-4 ${token.paused ? "border-amber-300" : "border-slate-200"}`}>
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${token.paused ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
            {token.paused ? <Pause className="w-4 h-4" strokeWidth={1.75} /> : <Play className="w-4 h-4" strokeWidth={1.75} />}
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight flex items-center gap-2">
              Global transfer switch
              <Badge variant="outline" className={`font-mono text-[10px] uppercase ${token.paused ? "border-amber-300 text-amber-700 bg-amber-50" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
                {token.paused ? "PAUSED" : "ACTIVE"}
              </Badge>
            </div>
            <div className="text-xs text-slate-500 mt-1 max-w-lg">
              {token.paused
                ? "All transfers, mints and burns are currently blocked. Unpause to restore normal operation."
                : "All transfers are operating normally. Pausing should only be used during incidents or audited maintenance."}
            </div>
          </div>
        </div>
        <PauseDialog paused={token.paused} onConfirm={actions.doPause} />
      </div>

      {/* Blacklist form */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
          <ShieldOff className="w-4 h-4 text-slate-700" strokeWidth={1.75} />
          <h3 className="text-sm font-semibold tracking-tight">Blacklist address</h3>
        </div>
        <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Address</Label>
            <Input data-testid={CONSOLE.blacklistAddrInput} value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x…" className="font-mono" />
          </div>
          <div className="md:col-span-5 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Reason / ticket #</Label>
            <Input data-testid={CONSOLE.blacklistReasonInput} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. OFAC-2026-0142" />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button
              data-testid={CONSOLE.blacklistSubmitBtn}
              disabled={!addr || submitting}
              onClick={async () => {
                setSubmitting(true);
                try {
                  await actions.doBlacklist({ address: addr, active: true, reason });
                  setAddr(""); setReason("");
                } catch (e) { toast.error("Blacklist failed", { description: e.message }); }
                finally { setSubmitting(false); }
              }}
              className="w-full rounded-md bg-slate-900 hover:bg-slate-800 text-white"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />}
              Freeze
            </Button>
          </div>
        </div>
      </div>

      {/* Blacklist table */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">Active blacklist</h3>
          <Badge variant="outline" className="font-mono text-[10px]">{activeList.length} addresses</Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Address</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Reason</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Frozen</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-slate-500">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeList.map((b) => (
              <TableRow key={b.id} data-testid={`blacklist-row-${b.address}`}>
                <TableCell><AddressDisplay value={b.address} short={false} /></TableCell>
                <TableCell className="text-sm text-slate-600">{b.reason || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500 font-mono">{timeAgo(b.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={CONSOLE.unblacklistBtn(b.address)}
                    variant="outline"
                    size="sm"
                    onClick={() => actions.doBlacklist({ address: b.address, active: false, reason: b.reason })}
                    className="rounded-md border-slate-200"
                  >
                    Unfreeze
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {activeList.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-sm text-slate-500 px-5 py-8 text-center">No addresses frozen.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PauseDialog({ paused, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setAck(false); }}>
      <DialogTrigger asChild>
        <Button
          data-testid={CONSOLE.pauseToggle}
          className={`rounded-md ${paused ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}
        >
          {paused ? <Play className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> : <Pause className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />}
          {paused ? "Unpause transfers" : "Pause transfers"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${paused ? "text-emerald-600" : "text-red-600"}`} strokeWidth={1.75} />
            {paused ? "Resume all transfers?" : "Halt all transfers?"}
          </DialogTitle>
          <DialogDescription>
            {paused
              ? "This will re-enable transfers, mints, and burns on the PUSD contract."
              : "This will block every transfer, mint, and burn on the PUSD contract until unpaused. Only use this during a verified incident."}
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <Checkbox checked={ack} onCheckedChange={(v) => setAck(Boolean(v))} />
          <span>I have authorization from the incident-response runbook and a logged ticket.</span>
        </label>
        <DialogFooter>
          <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid={CONSOLE.pauseConfirmBtn}
            disabled={!ack || busy}
            onClick={async () => {
              setBusy(true);
              try { await onConfirm(!paused); setOpen(false); }
              finally { setBusy(false); setAck(false); }
            }}
            className={`rounded-md ${paused ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            {paused ? "Confirm unpause" : "Confirm pause"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
